import { useMemo, useState, type ReactElement } from 'react';
import { Mail, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminEmailServerMutations,
  useAdminEmailServers,
  type EmailServerInput,
  type EmailServerViewModel,
} from '@/features/admin-email/api';
import { AdminEmailServersTable } from '@/features/admin-email/components/AdminEmailServersTable';
import { DeleteEmailServerConfirmDialog } from '@/features/admin-email/components/DeleteEmailServerConfirmDialog';
import { EmailServerFormDialog } from '@/features/admin-email/components/EmailServerFormDialog';
import { TestSendDialog } from '@/features/admin-email/components/TestSendDialog';

export function AdminEmailPage(): ReactElement {
  const { t } = useTranslation();
  const query = useAdminEmailServers();
  const { create, update, remove } = useAdminEmailServerMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState<EmailServerViewModel | null>(null);
  const [deleteRow, setDeleteRow] = useState<EmailServerViewModel | null>(null);
  const [testRow, setTestRow] = useState<EmailServerViewModel | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [query.isSuccess, query.data],
  );

  const isEdit = formInitial !== null;
  const formPending = isEdit ? update.isPending : create.isPending;
  const formError = isEdit ? update.error : create.error;

  function closeForm(): void {
    setFormOpen(false);
    setFormInitial(null);
    create.reset();
    update.reset();
  }

  function handleFormSubmit(input: EmailServerInput): void {
    if (formInitial !== null) {
      update.mutate({ ...input, id: formInitial.id }, { onSuccess: closeForm });
    } else {
      create.mutate(input, { onSuccess: closeForm });
    }
  }

  function handleDeleteConfirm(row: EmailServerViewModel): void {
    remove.mutate(row.id, {
      onSuccess: () => {
        setDeleteRow(null);
        remove.reset();
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<Mail className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.email.heading')}
        subtitle={t('routes.admin.email.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-email-servers-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 id="admin-email-servers-heading" className="text-base font-semibold text-foreground">
                {t('routes.admin.email.servers.heading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.email.servers.subheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                update.reset();
                setFormInitial(null);
                setFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.email.servers.addAction')}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.email.servers.error.heading')}
                message={t('routes.admin.email.servers.error.message')}
                codeLabel={t('routes.admin.email.servers.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.email.servers.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.email.servers.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.email.servers.heading')}>
              <AdminEmailServersTable
                rows={rows}
                onTest={(row) => {
                  setTestRow(row);
                }}
                onEdit={(row) => {
                  create.reset();
                  update.reset();
                  setFormInitial(row);
                  setFormOpen(true);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setDeleteRow(row);
                }}
                pendingDeleteId={remove.isPending && deleteRow !== null ? deleteRow.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <EmailServerFormDialog
        open={formOpen}
        initial={formInitial}
        onOpenChange={(next) => {
          if (!next && formPending) return;
          if (!next) closeForm();
          else setFormOpen(true);
        }}
        onSubmit={handleFormSubmit}
        pending={formPending}
        submitError={formError}
      />

      <DeleteEmailServerConfirmDialog
        row={deleteRow}
        onCancel={() => {
          if (remove.isPending) return;
          setDeleteRow(null);
          remove.reset();
        }}
        onConfirm={handleDeleteConfirm}
        pending={remove.isPending}
        submitError={remove.error}
      />

      <TestSendDialog
        server={testRow}
        onClose={() => {
          setTestRow(null);
        }}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
